import React, { useEffect, useState, useRef } from 'react';
import {
  getWorkOrders, getWorkOrder, createWorkOrder, updateWorkOrder, deleteWorkOrder,
  getTechnicians, getWorkOrderParts, getPartRequests, getPhotos, getParts,
  addPartToOrder, createPartRequest, updatePartRequest, deletePartRequest,
  uploadPhoto, deletePhoto,
} from '../api/client';
import toast from 'react-hot-toast';
import { usePermissions } from '../contexts/PermissionContext';
import { Plus, Search, Trash2, Paperclip, X, ArrowLeft } from 'lucide-react';

const STATUS_OPTIONS = ['open', 'assigned', 'in_progress', 'completed', 'cancelled'];
const PRIORITY_OPTIONS = ['low', 'normal', 'high', 'urgent'];
const EQUIPMENT_TYPES = ['HVAC', 'Commercial Refrigerator', 'Commercial Freezer', 'Ice Machine', 'Kitchen Equipment', 'Appliance', 'Other'];

const statusColor = {
  open: '#F59E0B', assigned: '#3B82F6', in_progress: '#10B981',
  completed: '#6B7280', cancelled: '#EF4444',
};
const priorityBg = {
  low: '#F0FDF4', normal: '#EFF6FF', high: '#FEF3C7', urgent: '#FEF2F2',
};

const EMPTY_FORM = {
  customer_name: '', business_name: '', contact_name: '',
  customer_phone: '', customer_email: '',
  address: '', country: '', city: '', state_province: '', zipcode: '',
  equipment_type: '', equipment_brand: '', equipment_model: '', equipment_serial: '',
  problem_description: '', priority: 'normal', labor_cost: 0, technician_id: '',
  warranty_status: '', extended_warranty: '', invoice_date: '',
  work_order_type: '', unit_description: '',
  prediagnosis_result: '', diagnosis_routes: '',
  technician_level: '', technician_type: '',
  tech_followup: '{}', dealer_followup: '{}', custom_followup: '{}',
  status: 'open',
};

function PartReqEditRow({ pr, onSave, onCancel }) {
  const [val, setVal] = React.useState({
    part_name: pr.part_name, part_code: pr.part_code || '',
    quantity: pr.quantity, tracking_number: pr.tracking_number || '', status: pr.status,
  });
  return (
    <tr style={{ background: '#FFFBEB', borderBottom: '1px solid #F3F4F6' }}>
      {['part_name', 'part_code', 'quantity', 'tracking_number'].map(k => (
        <td key={k} style={{ padding: '6px 8px' }}>
          <input value={val[k]} onChange={e => setVal({ ...val, [k]: e.target.value })}
            type={k === 'quantity' ? 'number' : 'text'} min={k === 'quantity' ? 1 : undefined}
            style={{ width: '100%', padding: '5px 8px', border: '1px solid #D1D5DB', borderRadius: 5, fontSize: 12 }} />
        </td>
      ))}
      <td style={{ padding: '6px 8px' }}>
        <select value={val.status} onChange={e => setVal({ ...val, status: e.target.value })}
          style={{ width: '100%', padding: '5px 8px', border: '1px solid #D1D5DB', borderRadius: 5, fontSize: 12 }}>
          {['pending', 'ordered', 'shipped', 'delivered'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td style={{ padding: '6px 8px' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => onSave(pr.id, val)} style={{ padding: '3px 8px', background: '#D1FAE5', color: '#065F46', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Save</button>
          <button onClick={onCancel} style={{ padding: '3px 8px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>✕</button>
        </div>
      </td>
    </tr>
  );
}

// ── Field helpers ─────────────────────────────────────────────────────────────
function FI({ label, disabled, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      <input {...props} disabled={disabled}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 7, fontSize: 13, background: disabled ? '#F9FAFB' : '#fff' }} />
    </div>
  );
}
function FS({ label, children, disabled, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      <select {...props} disabled={disabled}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 7, fontSize: 13, background: disabled ? '#F9FAFB' : '#fff' }}>
        {children}
      </select>
    </div>
  );
}
function FTA({ label, disabled, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      <textarea {...props} disabled={disabled} rows={3}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 7, fontSize: 13, background: disabled ? '#F9FAFB' : '#fff', resize: 'vertical' }} />
    </div>
  );
}
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];
const COUNTRIES = [
  'US','CA','MX','GB','AU','NZ','CN','JP','KR','SG','HK','TW','IN','DE','FR',
  'IT','ES','NL','SE','NO','DK','FI','CH','BE','AT','PT','PL','BR','AR','ZA',
];

// Parse "168 Rosswell St, Marietta, GA, IA 30060" or "168 Rosswell St, Marietta, GA 30060"
// → { street, city, state_province, zipcode, country }
function parseAddress(raw) {
  const parts = raw.trim().split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length < 3) return null;
  const street = parts[0];
  const city = parts[1];
  if (parts.length >= 5) {
    return { street, city, state_province: parts[2], zipcode: parts[3], country: parts[4] };
  }
  if (parts.length === 4) {
    // "street, city, ST, zip" where ST is 2-letter state
    if (/^[A-Za-z]{2}$/.test(parts[2])) {
      return { street, city, state_province: parts[2], zipcode: parts[3], country: 'US' };
    }
    // "street, city, ST ZIPCODE, country"
    const stateZip = parts[2].match(/^([A-Za-z]{2})\s+(.+)$/);
    if (stateZip) {
      return { street, city, state_province: stateZip[1], zipcode: stateZip[2], country: parts[3] };
    }
    return { street, city, state_province: parts[2], zipcode: parts[3], country: 'US' };
  }
  // 3 parts: "street, city, ST ZIPCODE" or "street, city, country"
  const stateZip = parts[2].match(/^([A-Za-z]{2})\s+(\S+)$/);
  if (stateZip) {
    return { street, city, state_province: stateZip[1], zipcode: stateZip[2], country: 'US' };
  }
  return { street, city, country: parts[2] };
}

// Compose full address from components
function composeAddress(street, city, state, zip, country) {
  const parts = [street, city, [state, zip].filter(Boolean).join(' '), country].filter(Boolean);
  return parts.join(', ');
}

function SectionTitle({ children }) {
  return (
    <div style={{ borderBottom: '2px solid #E5E7EB', marginBottom: 14, marginTop: 24, paddingBottom: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function WorkOrders() {
  const { hasPermission } = usePermissions();

  const canCreate = hasPermission('work_orders:create');
  const canEdit   = hasPermission('work_orders:edit');
  const canDelete = hasPermission('work_orders:delete');
  const canView   = hasPermission('work_orders:view');
  const canAddPart = hasPermission('work_orders:parts:add');
  const showActions = canEdit || canDelete || canView || canAddPart;

  // list state
  const [orders, setOrders] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // view: 'list' | 'detail'
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null); // null = new order

  // form
  const [form, setForm] = useState(EMPTY_FORM);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // parts used
  const [usedParts, setUsedParts] = useState([]);
  const [availParts, setAvailParts] = useState([]);
  const [partForm, setPartForm] = useState({ part_id: '', quantity_used: 1, unit_price: '' });
  const [savingPart, setSavingPart] = useState(false);
  const [partError, setPartError] = useState('');

  // part requests
  const [partReqs, setPartReqs] = useState([]);
  const [prForm, setPrForm] = useState({ part_name: '', part_code: '', quantity: 1, tracking_number: '', status: 'pending' });
  const [savingPr, setSavingPr] = useState(false);
  const [prError, setPrError] = useState('');
  const [editingPr, setEditingPr] = useState(null);

  // photos
  const [photos, setPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoManageMode, setPhotoManageMode] = useState(false);

  // complete confirm
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  // track which quick-action triggered the current submit ('dispatch' | 'complete' | null)
  const quickActionRef = useRef(null);

  // ── data loaders ─────────────────────────────────────────────────────────
  const loadList = async () => {
    setLoading(true);
    try {
      const [ordersRes, techsRes] = await Promise.all([
        getWorkOrders({ status: statusFilter || undefined }),
        getTechnicians().catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data || []);
      setTechs(techsRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(); }, [statusFilter]);

  const openDetail = async (order = null) => {
    setSelected(order);
    setFormError('');
    setPartError('');
    setPrError('');
    setPhotoError('');
    setUsedParts([]);
    setPartReqs([]);
    setPhotos([]);
    setAvailParts([]);
    setPartForm({ part_id: '', quantity_used: 1, unit_price: '' });
    setPrForm({ part_name: '', part_code: '', quantity: 1, tracking_number: '', status: 'pending' });
    setEditingPr(null);

    if (order) {
      setForm({ ...EMPTY_FORM, ...order, technician_id: order.technician_id || '' });
      const [partsRes, prRes, photosRes] = await Promise.all([
        getWorkOrderParts(order.id).catch(() => ({ data: [] })),
        getPartRequests(order.id).catch(() => ({ data: [] })),
        getPhotos(order.id).catch(() => ({ data: [] })),
      ]);
      setUsedParts(partsRes.data || []);
      setPartReqs(prRes.data || []);
      setPhotos(photosRes.data || []);
      if (canAddPart) {
        const av = await getParts().catch(() => ({ data: [] }));
        setAvailParts(av.data || []);
      }
    } else {
      setForm(EMPTY_FORM);
    }
    setView('detail');
  };

  const backToList = () => {
    setView('list');
    setSelected(null);
    loadList();
  };

  // ── form submit ───────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSaving(true);
    setFormError('');
    const data = { ...form, technician_id: form.technician_id || undefined, labor_cost: parseFloat(form.labor_cost) || 0 };
    try {
      if (selected) {
        await updateWorkOrder(selected.id, data);
        // Re-fetch to confirm saved data
        const refreshed = await getWorkOrder(selected.id);
        setSelected(refreshed.data);
        setForm({ ...EMPTY_FORM, ...refreshed.data, technician_id: refreshed.data.technician_id || '' });
        const action = quickActionRef.current;
        quickActionRef.current = null;
        const statusMsg = action === 'dispatch' ? 'Dispatched successfully' : action === 'complete' ? 'Work order completed' : 'Saved successfully';
        toast.success(statusMsg, { position: 'top-center' });
      } else {
        await createWorkOrder(data);
        toast.success('Work order created');
        backToList();
        return;
      }
      setFormError('');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save. Please try again.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setFormSaving(false);
    }
  };

  const handleQuickStatus = (status) => {
    if (status === 'assigned' && !form.technician_id) {
      toast.error('Please assign a technician before dispatching.', { position: 'top-center' });
      return;
    }
    quickActionRef.current = status === 'assigned' ? 'dispatch' : status === 'completed' ? 'complete' : null;
    setForm(prev => ({ ...prev, status }));
    setTimeout(() => document.getElementById('wo-form')?.requestSubmit(), 0);
  };

  // ── delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteWorkOrder(deleteTarget.id);
      setDeleteTarget(null);
      loadList();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Failed to delete.');
    } finally {
      setDeleting(false);
    }
  };

  // ── parts used ────────────────────────────────────────────────────────────
  const handleAddPart = async (e) => {
    e.preventDefault();
    if (!partForm.part_id || !selected) return;
    setSavingPart(true);
    setPartError('');
    try {
      await addPartToOrder(selected.id, {
        part_id: partForm.part_id,
        quantity_used: parseInt(partForm.quantity_used, 10),
        unit_price: parseFloat(partForm.unit_price),
      });
      const [partsRes, av] = await Promise.all([
        getWorkOrderParts(selected.id).catch(() => ({ data: [] })),
        getParts().catch(() => ({ data: [] })),
      ]);
      setUsedParts(partsRes.data || []);
      setAvailParts(av.data || []);
      setPartForm({ part_id: '', quantity_used: 1, unit_price: '' });
    } catch (err) {
      setPartError(err.response?.data?.detail || 'Failed to add part.');
    } finally {
      setSavingPart(false);
    }
  };

  // ── part requests ─────────────────────────────────────────────────────────
  const handleAddPartRequest = async (e) => {
    e.preventDefault();
    if (!prForm.part_name.trim() || !selected) return;
    setSavingPr(true);
    setPrError('');
    try {
      const res = await createPartRequest(selected.id, prForm);
      setPartReqs(prev => [...prev, res.data]);
      setPrForm({ part_name: '', part_code: '', quantity: 1, tracking_number: '', status: 'pending' });
    } catch (err) {
      setPrError(err.response?.data?.detail || 'Failed to add part request.');
    } finally {
      setSavingPr(false);
    }
  };
  const handleUpdatePartRequest = async (prId, data) => {
    try {
      const res = await updatePartRequest(selected.id, prId, data);
      setPartReqs(prev => prev.map(p => p.id === prId ? res.data : p));
      setEditingPr(null);
    } catch (err) {
      setPrError(err.response?.data?.detail || 'Failed to update.');
    }
  };
  const handleDeletePartRequest = async (prId) => {
    try {
      await deletePartRequest(selected.id, prId);
      setPartReqs(prev => prev.filter(p => p.id !== prId));
    } catch (err) {
      setPrError(err.response?.data?.detail || 'Failed to delete.');
    }
  };

  // ── photos ────────────────────────────────────────────────────────────────
  const handleUploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file || !selected) return;
    setPhotoUploading(true);
    setPhotoError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadPhoto(selected.id, fd);
      setPhotos(prev => [...prev, res.data]);
    } catch (err) {
      setPhotoError(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setPhotoUploading(false);
      e.target.value = '';
    }
  };
  const handleDeletePhoto = async (photoId) => {
    try {
      await deletePhoto(selected.id, photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch {
      setPhotoError('Failed to delete attachment.');
    }
  };

  const filtered = orders.filter(o =>
    !search ||
    (o.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (o.job_number || '').includes(search)
  );
  const colCount = showActions ? 8 : 7;
  const readOnly = !!selected && !canEdit;

  // ── LIST VIEW ─────────────────────────────────────────────────────────────
  if (view === 'list') return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>Work Orders</h1>
        {canCreate && (
          <button onClick={() => openDetail()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={16} /> New Job
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search jobs..."
            style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Job #', 'Customer', 'Equipment', 'Address', 'Status', 'Priority', 'Technician', ...(showActions ? ['Actions'] : [])].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={colCount} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>No work orders found</td></tr>
            ) : filtered.map(order => {
              const tech = techs.find(t => t.id === order.technician_id);
              return (
                <tr key={order.id} style={{ borderBottom: '1px solid #F3F4F6', background: priorityBg[order.priority] || '#fff' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 700, fontSize: 13, color: '#2563EB' }}>{order.job_number}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13 }}>
                    <div style={{ fontWeight: 600 }}>{order.customer_name}</div>
                    <div style={{ color: '#6B7280', fontSize: 11 }}>{order.customer_phone}</div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{order.equipment_type || '—'}</td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#6B7280', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.address}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${statusColor[order.status] || '#D1D5DB'}`, color: statusColor[order.status] || '#374151', fontSize: 12, fontWeight: 600, display: 'inline-block' }}>
                      {order.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: order.priority === 'urgent' ? '#FEE2E2' : order.priority === 'high' ? '#FEF3C7' : '#F3F4F6', color: order.priority === 'urgent' ? '#DC2626' : order.priority === 'high' ? '#92400E' : '#374151' }}>
                      {order.priority}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{tech?.full_name || '—'}</td>
                  {showActions && (
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openDetail(order)}
                          style={{ padding: '5px 12px', background: '#EFF6FF', color: '#2563EB', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          {canEdit ? 'Edit' : 'View'}
                        </button>
                        {canDelete && (
                          <button onClick={() => { setDeleteTarget(order); setDeleteError(''); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                            <Trash2 size={12} /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete confirm */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 420, width: '100%', margin: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Delete Work Order?</h3>
            <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 24 }}>
              Are you sure you want to delete <strong>{deleteTarget.job_number}</strong> ({deleteTarget.customer_name})? This cannot be undone.
            </p>
            {deleteError && <p style={{ color: '#DC2626', fontSize: 13, marginBottom: 16, padding: '8px 12px', background: '#FEF2F2', borderRadius: 6 }}>{deleteError}</p>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteTarget(null)} disabled={deleting}
                style={{ padding: '10px 20px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ padding: '10px 20px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: 8, cursor: deleting ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: deleting ? 0.7 : 1 }}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── DETAIL / CREATE VIEW ──────────────────────────────────────────────────
  return (
    <div>
      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <button onClick={backToList}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#2563EB', fontWeight: 600, fontSize: 14 }}>
          <ArrowLeft size={16} /> Back to Work Orders
        </button>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
          {selected ? `${selected.job_number} — ${selected.customer_name}` : 'New Work Order'}
        </h2>
        <div style={{ width: 160 }} />
      </div>

      <form id="wo-form" onSubmit={handleSubmit}>
        {/* Error */}
        {formError && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            {formError}
          </div>
        )}

        {/* Two-column layout: left = form, right = parts+attachments */}
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 24 }}>

          {/* ── LEFT: form fields ── */}
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: 24 }}>

            <SectionTitle>Customer Info</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <FI label="Business Name" disabled={readOnly} value={form.business_name || ''} onChange={e => setForm({ ...form, business_name: e.target.value })} />
              <FI label="Contact Name" disabled={readOnly} value={form.contact_name || ''} onChange={e => setForm({ ...form, contact_name: e.target.value })} />
              <FI label="Phone" disabled={readOnly} value={form.customer_phone || ''} onChange={e => setForm({ ...form, customer_phone: e.target.value })} />
              <FI label="Email" type="email" disabled={readOnly} value={form.customer_email || ''} onChange={e => setForm({ ...form, customer_email: e.target.value })} />
            </div>

            {/* Address block */}
            {/* 1. Paste helper — uncontrolled, clears after parse */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                Paste Full Address (auto-fills fields below)
              </label>
              <input
                disabled={readOnly}
                placeholder="e.g. 168 Rosswell St, Marietta, GA, 30060"
                value={[form.address, form.city, form.state_province, form.zipcode, form.country].filter(Boolean).join(', ')}
                onChange={e => {
                  const parsed = parseAddress(e.target.value);
                  if (parsed) {
                    setForm(prev => ({
                      ...prev,
                      address:        parsed.street         || prev.address,
                      city:           parsed.city           || prev.city,
                      state_province: parsed.state_province || prev.state_province,
                      zipcode:        parsed.zipcode        || prev.zipcode,
                      country:        parsed.country        || prev.country,
                    }));
                  }
                }}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 7, fontSize: 13, background: readOnly ? '#F9FAFB' : '#fff' }}
              />
            </div>
            {/* 2. Country | Street  /  City | State  /  Zipcode */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <FS label="Country" disabled={readOnly} value={form.country || ''}
                onChange={e => setForm({ ...form, country: e.target.value })}>
                <option value="">Select country</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </FS>
              <FI label="Street" disabled={readOnly} value={form.address || ''}
                onChange={e => setForm({ ...form, address: e.target.value })} />
              <FI label="City" disabled={readOnly} value={form.city || ''}
                onChange={e => setForm({ ...form, city: e.target.value })} />
              <FS label="State / Province" disabled={readOnly} value={form.state_province || ''}
                onChange={e => setForm({ ...form, state_province: e.target.value })}>
                <option value="">Select state</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </FS>
              <FI label="Zip Code" disabled={readOnly} value={form.zipcode || ''}
                onChange={e => setForm({ ...form, zipcode: e.target.value })} />
            </div>

            <SectionTitle>Equipment</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <FS label="Equipment Type" disabled={readOnly} value={form.equipment_type || ''} onChange={e => setForm({ ...form, equipment_type: e.target.value })}>
                <option value="">Select type</option>
                {EQUIPMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </FS>
              <FI label="Brand" disabled={readOnly} value={form.equipment_brand || ''} onChange={e => setForm({ ...form, equipment_brand: e.target.value })} />
              <FI label="Model" disabled={readOnly} value={form.equipment_model || ''} onChange={e => setForm({ ...form, equipment_model: e.target.value })} />
              <FI label="Serial Number" disabled={readOnly} value={form.equipment_serial || ''} onChange={e => setForm({ ...form, equipment_serial: e.target.value })} />
              <FI label="Work Order Type" disabled={readOnly} value={form.work_order_type || ''} onChange={e => setForm({ ...form, work_order_type: e.target.value })} />
              <FI label="Unit Description" disabled={readOnly} value={form.unit_description || ''} onChange={e => setForm({ ...form, unit_description: e.target.value })} />
            </div>

            <SectionTitle>Warranty</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
              <FI label="Warranty Status" disabled={readOnly} value={form.warranty_status || ''} onChange={e => setForm({ ...form, warranty_status: e.target.value })} />
              <FI label="Extended Warranty" disabled={readOnly} value={form.extended_warranty || ''} onChange={e => setForm({ ...form, extended_warranty: e.target.value })} />
              <FI label="Invoice Date" type="date" disabled={readOnly} value={form.invoice_date || ''} onChange={e => setForm({ ...form, invoice_date: e.target.value })} />
            </div>

            <SectionTitle>Diagnosis</SectionTitle>
            <FTA label="Problem Description *" required disabled={readOnly} value={form.problem_description || ''} onChange={e => setForm({ ...form, problem_description: e.target.value })} />
            <FTA label="Diagnosis" disabled={readOnly} value={form.diagnosis || ''} onChange={e => setForm({ ...form, diagnosis: e.target.value })} />
            <FTA label="Resolution" disabled={readOnly} value={form.resolution || ''} onChange={e => setForm({ ...form, resolution: e.target.value })} />

            {/* Assignment — only shown in left column when creating a new job (no right column) */}
            {!selected && (
              <>
                <SectionTitle>Assignment</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <FS label="Status" disabled={readOnly} value={form.status || 'open'} onChange={e => setForm({ ...form, status: e.target.value })}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </FS>
                  <FS label="Priority" disabled={readOnly} value={form.priority || 'normal'} onChange={e => setForm({ ...form, priority: e.target.value })}>
                    {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </FS>
                  <FS label="Technician" disabled={readOnly} value={form.technician_id || ''} onChange={e => setForm({ ...form, technician_id: e.target.value })}>
                    <option value="">Unassigned</option>
                    {techs.map(t => <option key={t.id} value={t.id}>{t.full_name} ({t.role})</option>)}
                  </FS>
                  <FI label="Labor Cost ($)" type="number" min="0" step="0.01" disabled={readOnly} value={form.labor_cost ?? 0} onChange={e => setForm({ ...form, labor_cost: e.target.value })} />
                  <FI label="Technician Level" disabled={readOnly} value={form.technician_level || ''} onChange={e => setForm({ ...form, technician_level: e.target.value })} />
                  <FI label="Technician Type" disabled={readOnly} value={form.technician_type || ''} onChange={e => setForm({ ...form, technician_type: e.target.value })} />
                </div>
              </>
            )}
          </div>

          {/* ── RIGHT: assignment + parts + attachments (existing orders only) ── */}
          {selected && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Assignment */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: 24 }}>
                <SectionTitle>Assignment</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <FS label="Status" disabled={readOnly} value={form.status || 'open'} onChange={e => setForm({ ...form, status: e.target.value })}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </FS>
                  <FS label="Priority" disabled={readOnly} value={form.priority || 'normal'} onChange={e => setForm({ ...form, priority: e.target.value })}>
                    {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </FS>
                  <FS label="Technician" disabled={readOnly} value={form.technician_id || ''} onChange={e => setForm({ ...form, technician_id: e.target.value })}>
                    <option value="">Unassigned</option>
                    {techs.map(t => <option key={t.id} value={t.id}>{t.full_name} ({t.role})</option>)}
                  </FS>
                  <FI label="Labor Cost ($)" type="number" min="0" step="0.01" disabled={readOnly} value={form.labor_cost ?? 0} onChange={e => setForm({ ...form, labor_cost: e.target.value })} />
                  <FI label="Technician Level" disabled={readOnly} value={form.technician_level || ''} onChange={e => setForm({ ...form, technician_level: e.target.value })} />
                  <FI label="Technician Type" disabled={readOnly} value={form.technician_type || ''} onChange={e => setForm({ ...form, technician_type: e.target.value })} />
                </div>
              </div>

              {/* Parts Used */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: 24 }}>
                <SectionTitle>Parts Used</SectionTitle>
                {usedParts.length === 0 ? (
                  <p style={{ color: '#9CA3AF', fontSize: 13, padding: '8px 0' }}>No parts used yet.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#F9FAFB' }}>
                        {['Part', 'Qty', 'Unit Price', 'Total'].map(h => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 12, borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {usedParts.map(p => (
                        <tr key={p.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                          <td style={{ padding: '8px 10px', fontWeight: 600 }}>{p.part_name}</td>
                          <td style={{ padding: '8px 10px' }}>{p.quantity_used}</td>
                          <td style={{ padding: '8px 10px' }}>${Number(p.unit_price || 0).toFixed(2)}</td>
                          <td style={{ padding: '8px 10px', fontWeight: 700 }}>${Number(p.total_price || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {partError && <p style={{ color: '#DC2626', fontSize: 13, marginBottom: 8, padding: '8px 12px', background: '#FEF2F2', borderRadius: 6 }}>{partError}</p>}
                {canAddPart && (
                  <form onSubmit={handleAddPart}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 100px', gap: 8, alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Part</label>
                        <select required value={partForm.part_id}
                          onChange={e => { const p = availParts.find(x => x.id === e.target.value); setPartForm({ ...partForm, part_id: e.target.value, unit_price: p?.unit_price ?? '' }); }}
                          style={{ width: '100%', padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 12 }}>
                          <option value="">Select part</option>
                          {availParts.map(p => <option key={p.id} value={p.id}>{p.name} ({p.quantity_in_stock})</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Qty</label>
                        <input type="number" min="1" required value={partForm.quantity_used} onChange={e => setPartForm({ ...partForm, quantity_used: e.target.value })}
                          style={{ width: '100%', padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 12 }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Unit Price</label>
                        <input type="number" min="0" step="0.01" required value={partForm.unit_price} onChange={e => setPartForm({ ...partForm, unit_price: e.target.value })}
                          style={{ width: '100%', padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 12 }} />
                      </div>
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                      <button type="submit" disabled={savingPart}
                        style={{ padding: '7px 16px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 7, cursor: savingPart ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 12, opacity: savingPart ? 0.7 : 1 }}>
                        {savingPart ? 'Adding...' : 'Add Part'}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Part Requests */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: 24 }}>
                <SectionTitle>Part Requests</SectionTitle>
                {prError && <p style={{ color: '#DC2626', fontSize: 13, marginBottom: 8, padding: '8px 12px', background: '#FEF2F2', borderRadius: 6 }}>{prError}</p>}
                {partReqs.length === 0 ? (
                  <p style={{ color: '#9CA3AF', fontSize: 13, padding: '8px 0' }}>No part requests yet.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#F9FAFB' }}>
                        {['Part Name', 'Code', 'Qty', 'Tracking #', 'Status', ...(canEdit ? [''] : [])].map(h => (
                          <th key={h} style={{ padding: '7px 8px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11, borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {partReqs.map(pr =>
                        editingPr === pr.id ? (
                          <PartReqEditRow key={pr.id} pr={pr} onSave={handleUpdatePartRequest} onCancel={() => setEditingPr(null)} />
                        ) : (
                          <tr key={pr.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                            <td style={{ padding: '7px 8px', fontWeight: 600 }}>{pr.part_name}</td>
                            <td style={{ padding: '7px 8px', color: '#6B7280' }}>{pr.part_code || '—'}</td>
                            <td style={{ padding: '7px 8px' }}>{pr.quantity}</td>
                            <td style={{ padding: '7px 8px', color: '#6B7280' }}>{pr.tracking_number || '—'}</td>
                            <td style={{ padding: '7px 8px' }}>
                              <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: pr.status === 'delivered' ? '#D1FAE5' : pr.status === 'shipped' ? '#DBEAFE' : pr.status === 'ordered' ? '#FEF3C7' : '#F3F4F6', color: pr.status === 'delivered' ? '#065F46' : pr.status === 'shipped' ? '#1D4ED8' : pr.status === 'ordered' ? '#92400E' : '#374151' }}>
                                {pr.status}
                              </span>
                            </td>
                            {canEdit && (
                              <td style={{ padding: '7px 8px' }}>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <button onClick={() => setEditingPr(pr.id)} style={{ padding: '2px 7px', background: '#EFF6FF', color: '#2563EB', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>Edit</button>
                                  <button onClick={() => handleDeletePartRequest(pr.id)} style={{ padding: '2px 7px', background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>Del</button>
                                </div>
                              </td>
                            )}
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                )}
                {canEdit && (
                  <form onSubmit={handleAddPartRequest}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 50px 1fr', gap: 6, alignItems: 'end' }}>
                      {[{ label: 'Part Name *', key: 'part_name', required: true }, { label: 'Part Code', key: 'part_code' }, { label: 'Qty', key: 'quantity', type: 'number', min: '1' }, { label: 'Tracking #', key: 'tracking_number' }].map(({ label, key, ...rest }) => (
                        <div key={key}>
                          <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: '#374151', marginBottom: 2 }}>{label}</label>
                          <input value={prForm[key]} onChange={e => setPrForm({ ...prForm, [key]: e.target.value })} {...rest}
                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: 5, fontSize: 11 }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, alignItems: 'end', marginTop: 6 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: '#374151', marginBottom: 2 }}>Status</label>
                        <select value={prForm.status} onChange={e => setPrForm({ ...prForm, status: e.target.value })}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: 5, fontSize: 11 }}>
                          {['pending', 'ordered', 'shipped', 'delivered'].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button type="submit" disabled={savingPr}
                          style={{ width: '100%', padding: '7px 12px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 6, cursor: savingPr ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 11, opacity: savingPr ? 0.7 : 1 }}>
                          {savingPr ? 'Adding...' : 'Add Request'}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>

              {/* Attachments */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <SectionTitle style={{ margin: 0 }}>Attachments ({photos.length})</SectionTitle>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => setPhotoManageMode(m => !m)}
                      style={{ padding: '6px 14px', background: photoManageMode ? '#FEF2F2' : '#F3F4F6', color: photoManageMode ? '#DC2626' : '#374151', border: photoManageMode ? '1px solid #FECACA' : '1px solid #E5E7EB', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >
                      {photoManageMode ? 'Done' : 'Manage'}
                    </button>
                    {canEdit && (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: photoUploading ? '#93C5FD' : '#2563EB', color: '#fff', borderRadius: 7, cursor: photoUploading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 12 }}>
                        <Paperclip size={13} />
                        {photoUploading ? 'Uploading...' : 'Upload'}
                        <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" style={{ display: 'none' }} disabled={photoUploading} onChange={handleUploadPhoto} />
                      </label>
                    )}
                  </div>
                </div>
                {photoError && <p style={{ color: '#DC2626', fontSize: 13, marginBottom: 8, padding: '8px 12px', background: '#FEF2F2', borderRadius: 6 }}>{photoError}</p>}
                {photos.length === 0 ? (
                  <p style={{ color: '#9CA3AF', fontSize: 13, padding: '8px 0' }}>No attachments yet.</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, marginBottom: 12 }}>
                    {photos.map(p => {
                      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(p.url || '');
                      const fullUrl = p.url?.startsWith('http') ? p.url : `https://service-tech-backend.onrender.com${p.url}`;
                      return (
                        <div key={p.id} style={{ border: photoManageMode ? '2px solid #FECACA' : '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                          {isImage ? (
                            <img src={fullUrl} alt={p.caption || ''}
                              onClick={() => window.open(fullUrl, '_blank')}
                              style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }} />
                          ) : (
                            <a href={fullUrl} target="_blank" rel="noopener noreferrer"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 90, background: '#F3F4F6', textDecoration: 'none', color: '#374151', fontSize: 11, gap: 4 }}>
                              <Paperclip size={14} /> {(p.url || '').split('/').pop()}
                            </a>
                          )}
                          {photoManageMode && canEdit && (
                            <button onClick={() => handleDeletePhoto(p.id)}
                              style={{ display: 'block', width: '100%', background: '#FEE2E2', color: '#DC2626', border: 'none', padding: '6px 0', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                              Delete
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          )}
        </div>

        {/* ── Sticky action bar ── */}
        <div style={{
          position: 'sticky', bottom: 0, zIndex: 10,
          background: '#fff', borderTop: '1px solid #E5E7EB',
          padding: '14px 0', marginTop: 24,
          display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end',
        }}>
          {!readOnly && (
            <>
              <button type="submit" disabled={formSaving}
                style={{ padding: '10px 24px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: formSaving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14, opacity: formSaving ? 0.7 : 1 }}>
                {formSaving ? 'Saving...' : selected ? 'Save' : 'Create Job'}
              </button>
              {selected && (
                <>
                  <button type="button" onClick={() => handleQuickStatus('assigned')}
                    style={{ padding: '10px 20px', background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                    Dispatch
                  </button>
                  <button type="button" onClick={() => setShowCompleteConfirm(true)}
                    style={{ padding: '10px 20px', background: '#6366F1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                    Complete
                  </button>
                  {showCompleteConfirm && (
                    <div
                      onClick={() => setShowCompleteConfirm(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
                    />
                  )}
                  {showCompleteConfirm && (
                    <div style={{
                      position: 'fixed', bottom: 80, right: 32, zIndex: 9999,
                      background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10,
                      boxShadow: '0 8px 28px rgba(0,0,0,0.18)', padding: '18px 22px', width: 260,
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 6 }}>Complete this job?</div>
                      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16, lineHeight: 1.5 }}>
                        This will mark the work order as completed and cannot be undone.
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button
                          type="button"
                          onClick={() => { setShowCompleteConfirm(false); handleQuickStatus('completed'); }}
                          style={{ flex: 1, padding: '8px 0', borderRadius: 7, border: 'none', background: '#059669', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                        >Yes</button>
                        <button
                          type="button"
                          onClick={() => setShowCompleteConfirm(false)}
                          style={{ flex: 1, padding: '8px 0', borderRadius: 7, border: '1px solid #D1D5DB', background: '#fff', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                        >No</button>
                      </div>
                    </div>
                  )}
                  <button type="button" onClick={() => setForm(prev => ({ ...prev, work_order_type: 'Replacement', status: 'open' }))}
                    style={{ padding: '10px 20px', background: '#F59E0B', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                    Replacement
                  </button>
                </>
              )}
              <button type="button" onClick={backToList}
                style={{ padding: '10px 20px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                Cancel
              </button>
            </>
          )}
          {readOnly && (
            <button type="button" onClick={backToList}
              style={{ padding: '10px 20px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
              Back
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
